import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Warehouse,
  Package,
  AlertTriangle,
  Calendar,
  ChevronDown,
  ChevronRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { motion, AnimatePresence } from "framer-motion";

const StockPage = () => {
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedProducts, setExpandedProducts] = useState({});

  useEffect(() => {
    fetchStock();
  }, []);

  const fetchStock = async () => {
    try {
      const res = await axios.get(`${API}/stock`);
      setStock(res.data);
    } catch (err) {
      toast.error("Fehler beim Laden des Lagerbestands");
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (productId) => {
    setExpandedProducts(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "empty": return "bg-slate-100 text-slate-600";
      case "low": return "bg-amber-100 text-amber-700";
      case "ok": return "bg-emerald-100 text-emerald-700";
      default: return "bg-slate-100 text-slate-600";
    }
  };

  const getExpiryColor = (expiryStatus) => {
    switch (expiryStatus) {
      case "expired": return "bg-red-100 text-red-700 border-red-300";
      case "critical": return "bg-orange-100 text-orange-700 border-orange-300";
      case "warning": return "bg-amber-100 text-amber-700 border-amber-300";
      default: return "bg-slate-100 text-slate-600 border-slate-300";
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("de-DE");
  };

  const getDaysUntil = (dateStr) => {
    if (!dateStr) return null;
    const days = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
    return days;
  };

  // Summary stats
  const totalProducts = stock.length;
  const lowStock = stock.filter(s => s.status === "low").length;
  const emptyStock = stock.filter(s => s.status === "empty").length;
  const expiringStock = stock.filter(s => s.expiry_status === "critical" || s.expiry_status === "warning").length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="stock-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight">
          Lagerbestand
        </h1>
        <p className="text-slate-500 mt-1">
          Übersicht aller Produkte im Lager mit MHD-Tracking
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Produkte</p>
                <p className="text-2xl font-bold">{totalProducts}</p>
              </div>
              <Package className="w-8 h-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-600">Niedriger Bestand</p>
                <p className="text-2xl font-bold text-amber-700">{lowStock}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600">Leer</p>
                <p className="text-2xl font-bold text-red-700">{emptyStock}</p>
              </div>
              <Warehouse className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-600">MHD kritisch</p>
                <p className="text-2xl font-bold text-orange-700">{expiringStock}</p>
              </div>
              <Calendar className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stock List */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Warehouse className="w-5 h-5" />
            Lagerübersicht
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {stock.length === 0 ? (
              <p className="text-center text-slate-500 py-8">Keine Produkte im Lager</p>
            ) : (
              stock.map((item) => (
                <div key={item.product_id} className="border rounded-lg overflow-hidden">
                  {/* Product Row */}
                  <div
                    className={`flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                      item.batches.length > 0 ? '' : 'opacity-60'
                    }`}
                    onClick={() => item.batches.length > 0 && toggleExpand(item.product_id)}
                  >
                    <div className="flex items-center gap-4">
                      {item.batches.length > 0 ? (
                        expandedProducts[item.product_id] ? (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        )
                      ) : (
                        <div className="w-5" />
                      )}
                      <div>
                        <p className="font-medium">{item.product_name}</p>
                        <p className="text-sm text-slate-500">
                          Min: {item.min_stock} Stück
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      {/* Expiry Badge */}
                      {item.earliest_expiry && (
                        <Badge className={getExpiryColor(item.expiry_status)}>
                          <Calendar className="w-3 h-3 mr-1" />
                          {formatDate(item.earliest_expiry)}
                          {getDaysUntil(item.earliest_expiry) !== null && (
                            <span className="ml-1">
                              ({getDaysUntil(item.earliest_expiry)}T)
                            </span>
                          )}
                        </Badge>
                      )}

                      {/* Quantity */}
                      <div className="text-right">
                        <p className="font-mono font-bold text-lg">
                          {item.total_quantity}
                        </p>
                        <Badge className={getStatusColor(item.status)}>
                          {item.status === "empty" ? "Leer" :
                           item.status === "low" ? "Niedrig" : "OK"}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {/* Batches (Expanded) */}
                  <AnimatePresence>
                    {expandedProducts[item.product_id] && item.batches.length > 0 && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="border-t bg-slate-50"
                      >
                        <div className="p-4 space-y-2">
                          <p className="text-sm font-medium text-slate-600 mb-2">
                            Chargen (FIFO - älteste zuerst):
                          </p>
                          {item.batches.map((batch, idx) => {
                            const days = getDaysUntil(batch.expiry_date);
                            let batchStatus = "ok";
                            if (days !== null) {
                              if (days < 0) batchStatus = "expired";
                              else if (days <= 7) batchStatus = "critical";
                              else if (days <= 14) batchStatus = "warning";
                            }
                            
                            return (
                              <div
                                key={batch.id}
                                className={`flex items-center justify-between p-3 rounded-lg border ${
                                  batchStatus === "expired" ? "bg-red-50 border-red-200" :
                                  batchStatus === "critical" ? "bg-orange-50 border-orange-200" :
                                  batchStatus === "warning" ? "bg-amber-50 border-amber-200" :
                                  "bg-white border-slate-200"
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-slate-500">#{idx + 1}</span>
                                  <span className="font-medium">{batch.quantity} Stück</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  {batch.expiry_date ? (
                                    <Badge className={getExpiryColor(batchStatus)}>
                                      MHD: {formatDate(batch.expiry_date)}
                                      {days !== null && (
                                        <span className="ml-1">
                                          ({days < 0 ? `${Math.abs(days)}T überfällig` : `${days}T`})
                                        </span>
                                      )}
                                    </Badge>
                                  ) : (
                                    <span className="text-sm text-slate-400">Kein MHD</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StockPage;
